import React, { memo, useCallback, useEffect, useState } from "react";
import isEqual from "react-fast-compare";
import {
  IconButton,
  List,
  ListItem,
  Popover,
  Tooltip,
  Typography,
  Badge,
  Theme,
} from "@mui/material";
import { makeStyles, createStyles, withStyles } from "@mui/styles";
import { Notifications, Check, Close } from "@mui/icons-material";
import {
  useAcceptInvitationMutation,
  useGetUserInvitations,
  useRejectInvitationMutation,
} from "../../../mutationsQueries/user";
import { InvitationType } from "../../../types";
// @ts-ignore
import { usePrevious } from "react-hookedup";
import useEffectSuccessAndErrorSnackbar from "../../../hooks/useEffectSuccessAndErrorSnackbar";

const useStyles = makeStyles((theme: Theme) => ({
  disabledButton: {
    "& svg": {
      color: "rgba(255,255,255,0.6)",
    },
  },
  actionButton: {
    padding: theme.spacing(1),
    marginLeft: `${theme.spacing(1)} !important`,
  },
  title: {
    padding: `${theme.spacing(1)} ${theme.spacing(2)} 0`,
    color: theme.palette.text.secondary,
  },
  listItem: {
    display: "flex",
    justifyContent: "space-between",
  },
}));

const StyledBadge = withStyles(theme =>
  createStyles({
    badge: {
      top: 3,
    },
  })
)(Badge);

type PropTypes = {
  buttonClassname?: string;
  id: number | undefined;
};

const InvitationsPopup = ({ buttonClassname, id }: PropTypes) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement>();
  const { data: invitations }: { data: InvitationType[] | undefined } =
    useGetUserInvitations(id);
  const prevInvitations = usePrevious(invitations);
  const rejectInvitationMutation = useRejectInvitationMutation();
  const acceptInvitationMutation = useAcceptInvitationMutation();
  const classes = useStyles();

  useEffectSuccessAndErrorSnackbar({
    mutation: rejectInvitationMutation,
    errorMessage: "Invitation rejection failed!",
    successMessage: "Invitation successfully rejected!",
  });

  useEffectSuccessAndErrorSnackbar({
    mutation: acceptInvitationMutation,
    errorMessage: "Invitation accept failed!",
    successMessage: "Invitation successfully accepted!",
  });

  const onClose = useCallback(() => setAnchorEl(undefined), []);
  const openPopup = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => setAnchorEl(e.currentTarget),
    []
  );

  const onCloseConfirmPopup = useCallback(() => {
    setAnchorEl(undefined);
  }, []);

  useEffect(() => {
    if (!invitations?.length && prevInvitations?.length === 1) {
      onCloseConfirmPopup();
    }
  }, [prevInvitations, invitations, onCloseConfirmPopup]);

  const onRejectInvitation = useCallback(
    (invitationId: number) => {
      rejectInvitationMutation.mutate({ invitationId });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [onCloseConfirmPopup]
  );

  const onAcceptInvitation = useCallback((invitationId: number) => {
    acceptInvitationMutation.mutate({ invitationId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Tooltip title={"Notifications"}>
        <span>
          <IconButton
            className={buttonClassname}
            classes={{ disabled: classes.disabledButton }}
            disabled={!invitations?.length}
            onClick={openPopup}
          >
            <StyledBadge badgeContent={invitations?.length} color="error">
              <Notifications color={"secondary"} />
            </StyledBadge>
          </IconButton>
        </span>
      </Tooltip>
      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={onClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <Typography className={classes.title} variant={"subtitle2"}>
          Organization invitations
        </Typography>
        <List>
          {invitations?.map((item: InvitationType) => (
            <ListItem key={item.id} className={classes.listItem} dense={true}>
              <Typography>{item.organization_name}</Typography>
              <span>
                <Tooltip title={"Accept"}>
                  <IconButton
                    className={classes.actionButton}
                    edge="end"
                    onClick={() => onAcceptInvitation(item.id)}
                  >
                    <Check fontSize={"small"} />
                  </IconButton>
                </Tooltip>
                <Tooltip title={"Reject"}>
                  <IconButton
                    className={classes.actionButton}
                    edge="end"
                    onClick={() => onRejectInvitation(item.id)}
                  >
                    <Close fontSize={"small"} />
                  </IconButton>
                </Tooltip>
              </span>
            </ListItem>
          ))}
        </List>
      </Popover>
    </>
  );
};

export default memo(InvitationsPopup, isEqual);
